import React from "react"; // Removed { useState, useRef }
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { useNotifications } from "../../context/NotificationContext";

// --- Sound function
const playBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.value = 850;

    osc.connect(gain);
    gain.connect(ctx.destination);

    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.03);

    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);

    setTimeout(() => {
      osc.stop();
      ctx.close();
    }, 300);
  } catch (err) {
    console.log("Beep failed", err);
  }
};

// --- Group notifications by date
const groupByDate = (items) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const isSameDay = (d1, d2) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const groups = { Today: [], Yesterday: [], Older: [] };

  items.forEach((n) => {
    const d = new Date(n.createdAt);

    if (isSameDay(d, today)) groups.Today.push(n);
    else if (isSameDay(d, yesterday)) groups.Yesterday.push(n);
    else groups.Older.push(n);
  });

  return groups;
};

const NotificationDropdown = ({ onClose }) => {
  const navigate = useNavigate();
  const {
    notifications,
    markOneAsReadLocal,
    markAllAsReadLocal,
    deleteOneLocal,
    clearAllLocal,
    isLoading,
    // Note: setNotifications was likely here previously; removed to clear Line 62 error
  } = useNotifications();

  const groups = groupByDate(notifications);

  const handleMarkOne = async (id) => {
    try {
      await axiosInstance.post(`/users/notifications/${id}/read`);
      markOneAsReadLocal(id);
    } catch (err) {
      console.log("Single read error", err);
    }
  };

  const handleMarkAll = async () => {
    try {
      await axiosInstance.post("/users/notifications/read-all");
      markAllAsReadLocal();
    } catch (err) {
      console.error("Mark all error", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      deleteOneLocal(id); // Optimistic UI
      await axiosInstance.delete(`/users/notifications/${id}`);
    } catch (err) {
      console.log("Delete error", err);
    }
  };

  const handleClearAll = async () => {
    try {
      clearAllLocal();
      await axiosInstance.delete(`/users/notifications`);
    } catch (err) {
      console.log("Clear error", err);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="w-full sm:w-[450px] max-h-[85vh] bg-white rounded-[2rem] shadow-2xl border border-slate-100 flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Notifications</h3>
          {notifications.filter(n => !n.isRead).length > 0 && (
            <span className="bg-red-50 text-red-600 text-[10px] font-bold px-2 py-0.5 rounded-md">
              {notifications.filter(n => !n.isRead).length} new
            </span>
          )}
        </div>
        <button onClick={handleClearAll} className="text-slate-400 hover:text-rose-500 text-xs font-bold transition-colors">
          Clear
        </button>
      </div>

      {/* Content */}
      <div className="overflow-y-auto overflow-x-hidden p-2 flex-1 max-h-[60vh]">
        {isLoading ? (
          <p className="text-center text-slate-400 text-xs font-bold py-8 uppercase tracking-widest">Loading...</p>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest">No notifications yet</p>
          </div>
        ) : (
          <>
            {["Today", "Yesterday", "Older"].map((section) => (
              groups[section].length > 0 && (
                <div key={section} className="mb-2">
                  <p className="text-[10px] uppercase font-black text-slate-300 tracking-[0.2em] px-4 py-2">{section}</p>

                  <AnimatePresence>
                    {groups[section].map((noti) => (
                      <motion.div
                        key={noti._id}
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className={`group relative p-4 flex gap-4 cursor-pointer transition-all border-b border-slate-50 last:border-0 hover:bg-slate-50 rounded-2xl
                          ${noti.isRead ? "opacity-75" : ""}
                        `}
                        onClick={() => {
                          if (!noti.isRead) handleMarkOne(noti._id);
                          if (noti.actionUrl) {
                            onClose && onClose();
                            navigate(noti.actionUrl);
                          }
                        }}
                        onAnimationStart={() => {
                          if (!noti.isRead) {
                            playBeep();
                            if (navigator.vibrate) navigator.vibrate(80);
                          }
                        }}
                      >
                        <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0 border border-blue-100/50">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path></svg>
                        </div>
                        <div className="flex-1 pr-6">
                          <h4 className="text-sm font-bold text-slate-800 leading-tight">
                            {noti.title}
                          </h4>
                          <p className="text-slate-500 text-[13px] mt-1 leading-relaxed">{noti.message}</p>
                          <p className="text-[10px] font-bold text-slate-400 mt-2 tracking-wide">
                            {new Date(noti.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })}
                          </p>
                        </div>

                        <button
                          className="absolute right-4 top-4 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(noti._id);
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )
            ))}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center p-4 bg-slate-50 border-t border-slate-100">
        <button
          onClick={onClose}
          className="text-[11px] font-bold text-slate-800 hover:text-blue-600 transition-colors uppercase tracking-widest"
        >
          View Notification Center
        </button>
        <button
          onClick={handleMarkAll}
          className="text-[11px] font-bold text-slate-500 hover:text-slate-800 transition-colors uppercase tracking-widest"
        >
          Mark All Read
        </button>
      </div>
    </motion.div>
  );
};

export default NotificationDropdown;